import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import SaveIcon from '@mui/icons-material/Save';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'components/TextField';

import { useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';

import {
  ServiceGroupsListAddMutation,
  ServiceGroupsListAddMutation$data,
} from '__generated__/ServiceGroupsListAddMutation.graphql';

type FormFields = {
  name: string;
};

type Props = {
  connectionId: string;
};

const initial = {
  name: '',
};

const ServiceGroupsListAdd: FC<Props> = ({ connectionId }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler<ServiceGroupsListAddMutation$data>();

  const [add, loading] = useMutation<ServiceGroupsListAddMutation>(graphql`
    mutation ServiceGroupsListAddMutation(
      $input: CreateServiceGroupInput!
      $connections: [ID!]!
    ) {
      createServiceGroup(input: $input) {
        node
          @prependNode(
            connections: $connections
            edgeTypeName: "ServiceGroupEdge"
          ) {
          ...ServiceGroupsListRow_serviceGroup
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { input, connections: [connectionId] },

        ...errorHandler({
          onCompleted: () => {
            close();
          },
        }),

        updater: store => {
          store.get(connectionId)?.invalidateRecord();
        },
      });
    },
    [add, close, connectionId, errorHandler]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas paslaugų paketas</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują paslaugų pakeitą
          </DialogContentText>

          <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={loading}
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default ServiceGroupsListAdd;
